import { Interest } from '@beconfident/api-lib';
import axios from 'axios';
import _ from 'lodash';
import { defineStore } from 'pinia';

import notifyError from '@/helpers/notify-error';
import logger from '@/infra/logger';
import { Onboarding, Settings } from '@/types/onboarding';

type State = {
  progress: number;
  loading: boolean;
  purchaseId: string;
  remainingQuantity: number;
  publicIds: string[];
  isLogin: boolean;
  whatsappVerified: boolean;
  onboarding: Onboarding;
};

export const useOnboardingStore = defineStore('onboarding', {
  persist: true,
  state: (): State => ({
    progress: 0,
    loading: false,
    purchaseId: '',
    remainingQuantity: 0,
    publicIds: [],
    isLogin: false,
    whatsappVerified: false,
    onboarding: {
      whatsapp: '',
      difficulties: [],
      level: '',
      code: '',
      email: '',
      profession: '',
      gender: '',
      age: '',
      name: '',
      interests: [],
      periodicityKind: 'multiple-times-in-a-day',
      periodicity: [],
      motivation: '',
      fluencyMonthsExpectation: '',
      settings: {
        voiceId: '',
        audioSpeed: 1,
        timezone: 'America/Sao_Paulo',
      },
    },
  }),

  actions: {
    setProgress(value: number) {
      this.progress = value;
    },
    async sendOTP({ whatsapp }: { whatsapp: string }) {
      try {
        const { data } = await axios.post('/onboarding/otp/send', {
          whatsapp,
        });

        const email = this.onboarding.email;
        const name = this.onboarding.name;

        if (data.whatsappVerified) {
          whatsapp = data.whatsappVerified;
        }

        const purchaseId = this.purchaseId;
        this.resetOnboarding();
        this.setOnboarding({
          ...this.onboarding,
          whatsapp,
          email,
          name,
        });
        this.setPurchaseId(purchaseId);

        return data;
      } catch (error) {
        logger.captureException(error);
        const errorCode = _.get(error, 'response.data.code', '');

        if ((errorCode as string) === 'BC:E:ALREADY_CREATED_USER') {
          this.resetOnboarding();

          return {
            error: 'BC:E:ALREADY_CREATED_USER',
          };
        }

        notifyError(
          error,
          'Não foi possível enviar o código para esse WhatsApp. Tente novamente.',
        );
      }
    },

    async validateOTP({ whatsapp, code }: { whatsapp: string; code: string }) {
      try {
        const { data } = await axios.post('/onboarding/otp/validate', {
          whatsapp,
          code,
        });

        this.setWhatsappVerified(true);

        return data;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Código inválido. Tente novamente.');
      }
    },

    async validatePurchase({ whatsapp }: { whatsapp: string }) {
      try {
        if (!this.purchaseId) {
          return;
        }

        const { data } = await axios.get(
          `/onboarding/${this.purchaseId}/validate`,
        );

        this.setOnboarding({
          ...this.onboarding,
          whatsapp: whatsapp ?? data.whatsapp,
          email: data.email,
          name: data.name,
        });

        return data;
      } catch (error) {
        logger.captureException(error);
        this.setPurchaseId('');

        const errorCode = _.get(error, 'response.data.code', '');

        if (errorCode) {
          return {
            error: errorCode,
          };
        }
      }
    },

    async completePurchase() {
      try {
        const { data } = await axios.post(
          `/onboarding/${this.purchaseId}/complete`,
          {
            ...this.onboarding,
          },
        );

        this.resetOnboarding();

        if (data.remainingQuantity === 0) {
          this.setRemainingQuantity(0);
          this.setPublicIds([]);
          this.setPurchaseId('');
        } else {
          this.setRemainingQuantity(data.remainingQuantity);
          this.setPublicIds(data.publicIds);
        }

        return data;
      } catch (error) {
        logger.captureException(error);
        const errorCode = _.get(error, 'response.data.code', '');

        if (errorCode) {
          return {
            error: errorCode,
          };
        }

        notifyError(
          error,
          'Erro ao finalizar sua ativação. Tente novamente ou entre em contato com o suporte.',
        );
      }
    },

    // todo:types
    async createPurchaseBySource({
      source = 'digital-manager-guru',
      externalPurchaseId,
    }: {
      source: any;
      externalPurchaseId: any;
    }) {
      const { data } = await axios.post(`/onboarding/purchase/${source}`, {
        id: externalPurchaseId,
      });

      this.setOnboarding({
        ...this.onboarding,
        whatsapp: data.whatsapp,
        email: data.email,
      });

      this.setPurchaseId(data.purchaseId);

      return data;
    },

    // todo:types
    async updatePurchaseBySource({
      externalPurchaseId,
      purchaseId,
      source = 'digital-manager-guru',
      email,
      whatsapp,
    }: any) {
      try {
        const { data } = await axios.put(`/onboarding/purchase/${source}`, {
          purchaseId,
          id: externalPurchaseId,
          email,
          whatsapp,
        });

        return data;
      } catch (error) {
        logger.captureException(error);
        notifyError(
          error,
          'Erro ao finalizar sua ativação. Tente novamente ou entre em contato com o suporte.',
        );
      }
    },

    async getPurchase({ email }: { email: string }) {
      const { data } = await axios.get(`/onboarding/purchase`, {
        params: {
          email,
        },
      });

      return data;
    },

    setOnboardingCode(value: string) {
      this.onboarding.code = value;
    },
    setOnboardingWhatsapp(value: string) {
      this.onboarding.whatsapp = value.replace('+', '');
    },
    setPurchaseId(purchaseId: string) {
      this.purchaseId = purchaseId;
    },
    setRemainingQuantity(remainingQuantity: number) {
      this.remainingQuantity = remainingQuantity;
    },
    setPublicIds(publicIds: string[]) {
      this.publicIds = publicIds;
    },
    setIsLogin(isLogin: boolean) {
      this.isLogin = isLogin;
    },
    setWhatsappVerified(whatsappVerified: boolean) {
      this.whatsappVerified = whatsappVerified;
    },
    setOnboarding(onboarding: Onboarding) {
      this.onboarding = onboarding;
    },
    resetOnboarding() {
      this.purchaseId = '';
      this.onboarding = {
        whatsapp: '',
        difficulties: [],
        level: '',
        email: '',
        profession: '',
        gender: '',
        code: '',
        age: '',
        name: '',
        interests: [],
        periodicityKind: 'multiple-times-in-a-day',
        periodicity: [],
        motivation: '',
        fluencyMonthsExpectation: '',
        settings: {
          voiceId: '',
          audioSpeed: 1,
          timezone: 'America/Sao_Paulo',
        },
      };
    },
    setLoading(loading: boolean) {
      this.loading = loading;
    },
    setAge(age: string) {
      this.onboarding.age = age;
    },
    setDifficulties(difficulties: []) {
      this.onboarding.difficulties = difficulties;
    },
    setFluency(fluency: string) {
      this.onboarding.fluencyMonthsExpectation = fluency;
    },
    setGender(gender: string) {
      this.onboarding.gender = gender;
    },
    setName(name: string) {
      this.onboarding.name = name;
    },
    setOnboardingAge(age: string) {
      this.onboarding.age = age;
    },
    setOnboardingGender(gender: string) {
      this.onboarding.gender = gender;
    },
    setOnboardingProfession(value: string) {
      this.onboarding.profession = value;
    },
    setOnboardingDifficulties(arrValues: string[]) {
      this.onboarding.difficulties = arrValues;
    },
    setOnboardingExpectation(value: string) {
      this.onboarding.fluencyMonthsExpectation = value;
    },
    setOnboardingMotivation(value: string) {
      this.onboarding.motivation = value;
    },
    setEmail(email: string) {
      this.onboarding.email = email;
    },
    setWhatsapp(whatsapp: string) {
      this.onboarding.whatsapp = whatsapp;
    },
    setLevel(level: string) {
      this.onboarding.level = level;
    },
    setMotivation(motivation: string) {
      this.onboarding.motivation = motivation;
    },
    setSettings(settings: Settings) {
      this.onboarding.settings = settings;
    },
    setOnboardingInterests(interests: Interest[]) {
      this.onboarding.interests = interests;
    },
  },
});
